import type { SupportedLocale } from "@playbrain/content-types"
import { playdataSampleReportUrl } from "@playdata/playdata-assets"
import { createContext, type ReactNode, useContext, useMemo } from "react"

type PlaydataSettings = {
  sampleReportUrl: string | null
}

const PlaydataSettingsContext = createContext<PlaydataSettings | null>(null)

/**
 * Microsite settings, loaded once by the layout route.
 *
 * The sample report link is used by buttons on five pages. Loading it per page
 * would mean five loaders fetching the same singleton, so the layout resolves
 * it and the pages read it from here.
 */
export function PlaydataSettingsProvider({
  children,
  settings,
}: {
  children: ReactNode
  settings: PlaydataSettings | null
}) {
  const value = useMemo(() => settings, [settings])
  return <PlaydataSettingsContext.Provider value={value}>{children}</PlaydataSettingsContext.Provider>
}

/**
 * The sample report URL an editor has set, falling back to the one the site
 * ships with when settings are missing or unpublished on this stage.
 */
export function usePlaydataSampleReportUrl(locale: SupportedLocale): string {
  const settings = useContext(PlaydataSettingsContext)
  return playdataSampleReportUrl(locale, settings?.sampleReportUrl)
}
